import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { BehaviorSubject, Observable } from 'rxjs';
import firebase from "firebase/compat/app";
import 'firebase/compat/firestore';
import "firebase/compat/messaging";
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class MessagingService {
  private token: string;
  private token$: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private refreshInFlight: Promise<any> | null = null;
  private readonly refreshIntervalMs = 12 * 60 * 60 * 1000;
  private notifications$: BehaviorSubject<any> = new BehaviorSubject(null);
  constructor(
    private angularFireMessaging: AngularFireMessaging) {

    if (firebase.messaging.isSupported()) {
      if (!firebase.apps.length) {
        firebase.initializeApp(environment.firebaseConfig);
      } else {
        firebase.app();
      }

      this.requestToken().then((rtn: any) => {
        if (rtn.status) {
          this.addBackgroundListner();
        }
      });

      // Foreground FCM messages do not pass through onBackgroundMessage. Turn
      // them into OS notifications as well so a paired watch receives the same
      // alert while the Agent panel is open.
      this.angularFireMessaging.messages.subscribe((payload: any) => {
        this.setNotifications(this.normalizeForPanel(payload));
        this.showSystemNotification(payload);
      });

      // FCM web tokens can rotate without a page reload. Re-check when a dormant
      // tab becomes active and periodically while the panel stays open. Emitting
      // an unchanged token is intentional: the API uses it as a last-seen touch.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && Notification.permission === 'granted') {
          this.requestToken(false);
        }
      });
      setInterval(() => {
        if (Notification.permission === 'granted') {
          this.requestToken(false);
        }
      }, this.refreshIntervalMs);
    }
  }
  receiveBackgroundMessage(event) {
    this.setNotifications(this.normalizeForPanel(event.data));
  }

  addBackgroundListner() {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener('message', this.receiveBackgroundMessage.bind(this));
    }
  }

  removeBackgroundListner() {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener('message', this.receiveBackgroundMessage.bind(this));
    }
  }

  getNotifications(): Observable<any> {
    return this.notifications$.asObservable();
  }

  setNotifications(notification) {
    this.notifications$.next(notification);
  }

  private normalizeForPanel(payload: any) {
    if (!payload?.data?.event_type) return payload;
    return {
      ...payload,
      notification: {
        title: payload.data.event_type,
        body: payload.data.payload || '{}'
      }
    };
  }

  private showSystemNotification(payload: any) {
    if (Notification.permission !== 'granted') return;
    const presenter = (window as any).PanelPushNotification;
    if (!presenter || typeof presenter.format !== 'function') return;

    const display = presenter.format(payload);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then(registration => registration.showNotification(display.title, display.options))
        .catch(() => this.showWindowNotification(display));
      return;
    }
    this.showWindowNotification(display);
  }

  private showWindowNotification(display: any) {
    try {
      new Notification(display.title, display.options);
    } catch (_) {
      // The existing in-panel toast remains available if system notifications
      // are unsupported by this browser/device.
    }
  }

  getToken() {
    return this.token;
  }

  getTokenChanges(): Observable<string | null> {
    return this.token$.asObservable();
  }

  requestToken(showPermissionError = true) {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = new Promise((resolve) => {
      this.angularFireMessaging.requestToken.subscribe((token: any) => {
        if (token) {
          this.token = token;
          this.token$.next(token);
          resolve({ status: true, token: token });
          return;
        }
        resolve({ status: false, error: new Error('No notification token returned') });
      }, (error: any) => {
        if (showPermissionError) {
          console.warn("Unable to get permission to notify.", error);
        }
        resolve({ status: false, error: error });
      });
    }).finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }
}
